import { useCallback, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import type { ProfileResponse } from '@shared/api';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { DeleteAccountDialog } from '../components/DeleteAccountDialog';
import { PinPad } from '../components/PinPad';
import { Notice } from '../components/Notice';
import { FullPageLoading } from '../components/Spinner';
import { formatPrice } from '../components/UpgradeDialog';
import { errorMessage, get, post, put } from '../lib/api';
import { usePinFlow } from '../lib/usePinFlow';
import { useSession } from '../state/session';

const PLAN_LABEL = { free_trial: 'Período grátis', standard: 'Standard', free: 'Gratuito' } as const;
const STATUS_LABEL = { active: 'Ativo', inactive: 'Pagamento pendente', cancelled: 'Cancelado', pending: 'Aguardando confirmação' } as const;

function formatDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
}

export function PageShell({ title, children, back = '/' }: { title: string; children: ReactNode; back?: string }) {
  return (
    <div className="page">
      <header className="page__header">
        <Link href={back} className="btn btn--ghost btn--small">
          ← Voltar
        </Link>
        <h1>{title}</h1>
      </header>
      <main className="page__body">{children}</main>
    </div>
  );
}

export function ProfilePage() {
  const { config, refresh, logout, offline } = useSession();
  const [, navigate] = useLocation();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [changingPin, setChangingPin] = useState<'current' | 'new' | null>(null);
  const [currentPin, setCurrentPin] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const loadProfile = useCallback(() => {
    get<ProfileResponse>('/api/user/profile').then(setProfile, (err) => setError(errorMessage(err)));
  }, []);
  // Same create/verify flow as the board's caregiver area; skipped if the PIN was entered recently.
  const pin = usePinFlow({ active: !offline, skipIfUnlocked: true, onSuccess: loadProfile });

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const changePin = (pin: string) => {
    if (changingPin === 'current') {
      setCurrentPin(pin);
      setChangingPin('new');
      return;
    }
    void run(async () => {
      try {
        await put('/api/user/pin', { pin, currentPin });
        setMessage('PIN alterado.');
      } finally {
        setChangingPin(null);
        setCurrentPin(null);
      }
    });
  };

  const redirectTo = (url: string) =>
    run(async () => {
      const { url: target } = await post<{ url: string }>(url);
      window.location.assign(target);
    });

  if (offline) {
    return (
      <PageShell title="Meu perfil">
        <Notice kind="warning">O perfil precisa de conexão com a internet.</Notice>
      </PageShell>
    );
  }

  if (pin.stage === 'loading') return <FullPageLoading />;

  if (pin.stage !== 'done') {
    return (
      <PageShell title="Meu perfil">
        <div className="card narrow">
          <p>
            {pin.stage === 'create'
              ? 'Crie um PIN de 4 dígitos. Ele protege os ajustes, o perfil e os dados de pagamento.'
              : 'Digite o PIN do responsável.'}
          </p>
          {pin.stage === 'error' ? (
            <button type="button" className="btn btn--secondary" onClick={() => void pin.retry()}>
              Tentar novamente
            </button>
          ) : (
            <PinPad
              label={pin.stage === 'create' ? (pin.confirming ? 'Repita o novo PIN' : 'Novo PIN') : 'PIN'}
              submitLabel="Confirmar PIN"
              busy={pin.busy}
              onSubmit={(value) => void pin.submit(value)}
            />
          )}
          {pin.error && <Notice kind="error">{pin.error}</Notice>}
        </div>
      </PageShell>
    );
  }

  if (!profile) return error ? <PageShell title="Meu perfil"><Notice kind="error">{error}</Notice></PageShell> : <FullPageLoading />;
  const billing = config?.billing;
  const price = billing?.price;

  return (
    <PageShell title="Meu perfil">
      {message && <Notice kind="success">{message}</Notice>}
      {error && <Notice kind="error">{error}</Notice>}

      <section className="card">
        <h2>Conta</h2>
        <dl className="details">
          <dt>Nome</dt>
          <dd>{profile.name}</dd>
          <dt>E-mail</dt>
          <dd>{profile.email}</dd>
          <dt>Membro desde</dt>
          <dd>{formatDate(profile.createdAt)}</dd>
        </dl>
      </section>

      <section className="card">
        <h2>Plano e assinatura</h2>
        {profile.isTest ? (
          <>
            <dl className="details">
              <dt>Plano</dt>
              <dd>Conta de teste</dd>
              <dt>Cobrança</dt>
              <dd>Nenhuma: acesso completo, sem prazo</dd>
            </dl>
            <Notice kind="info">Para passar ao plano normal (45 dias grátis e depois a assinatura), fale com o suporte.</Notice>
          </>
        ) : (
          <>
          <dl className="details">
            <dt>Plano</dt>
            <dd>
              {PLAN_LABEL[profile.effectivePlan]}
              {profile.effectivePlan !== profile.plan && ' (período grátis encerrado)'}
            </dd>
            <dt>Situação</dt>
            <dd>{STATUS_LABEL[profile.paymentStatus]}</dd>
            {profile.plan === 'free_trial' && (
              <>
                <dt>Fim do período grátis</dt>
                <dd>{formatDate(profile.trialEndsAt)}</dd>
              </>
            )}
            {price && (
              <>
                <dt>Valor do Standard</dt>
                <dd>{formatPrice(price.amount, price.currency)}/mês</dd>
              </>
            )}
            <dt>Cartão</dt>
            <dd>
              {profile.card
                ? `${profile.card.brand.toUpperCase()} •••• ${profile.card.last4} (validade ${String(profile.card.expMonth).padStart(2, '0')}/${profile.card.expYear})`
                : 'Nenhum cartão cadastrado'}
            </dd>
          </dl>

          {billing?.enabled ? (
            <div className="actions">
              {profile.effectivePlan !== 'standard' && !profile.hasSubscription && (
                <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void redirectTo('/api/billing/checkout')}>
                  Assinar Standard
                </button>
              )}
              <Link href="/adicionar-cartao" className="btn btn--secondary">
                {profile.card ? 'Atualizar cartão' : 'Adicionar cartão'}
              </Link>
              {profile.hasStripeCustomer && (
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void redirectTo('/api/billing/portal')}>
                  Gerenciar assinatura e faturas
                </button>
              )}
            </div>
          ) : (
            <Notice kind="info">Pagamentos ainda não estão disponíveis. Fale com o administrador.</Notice>
          )}
          </>
        )}
      </section>

      <section className="card">
        <h2>Segurança</h2>
        {changingPin ? (
          <div className="narrow">
            <PinPad
              label={changingPin === 'current' ? 'PIN atual' : 'Novo PIN'}
              submitLabel="Confirmar"
              busy={busy}
              onSubmit={changePin}
            />
            <button type="button" className="btn btn--ghost" onClick={() => setChangingPin(null)}>
              Cancelar
            </button>
          </div>
        ) : (
          <div className="actions">
            <button type="button" className="btn btn--secondary" onClick={() => setChangingPassword(true)}>
              {profile.hasPassword ? 'Trocar senha' : 'Criar senha'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={() => setChangingPin('current')}>
              Alterar PIN
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() =>
                void run(async () => {
                  await post('/api/user/profile/lock');
                  navigate('/');
                })
              }
            >
              Bloquear perfil e voltar
            </button>
            <button
              type="button"
              className="btn btn--danger-ghost"
              onClick={() =>
                void logout().then(() => {
                  void refresh();
                  navigate('/login');
                })
              }
            >
              Sair da conta
            </button>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Excluir conta</h2>
        <p className="muted">Apaga a conta e todos os dados do quadro. Se houver assinatura, ela é cancelada.</p>
        <div className="actions">
          <button type="button" className="btn btn--danger-ghost" onClick={() => setDeleting(true)}>
            Excluir minha conta
          </button>
        </div>
      </section>

      <ChangePasswordDialog
        open={changingPassword}
        hasPassword={profile.hasPassword}
        onClose={() => setChangingPassword(false)}
        onChanged={() => {
          setChangingPassword(false);
          setMessage(profile.hasPassword ? 'Senha alterada.' : 'Senha criada.');
          loadProfile();
        }}
      />
      <DeleteAccountDialog
        open={deleting}
        hasPassword={profile.hasPassword}
        hasSubscription={profile.hasSubscription}
        onClose={() => setDeleting(false)}
        onDeleted={() =>
          // The server already ended the session; this clears what is cached on this device.
          void logout().then(() => navigate('/login?conta=excluida', { replace: true }))
        }
      />
    </PageShell>
  );
}
